/**
 * Quran Page View Component
 * Mushaf-style layout using QCF V2 glyph-based rendering.
 * 
 * Modes:
 * - "read": Pure Mushaf layout — words grouped by lineNumber
 * - "tadabbur": Verse-by-verse layout with inline Thai tafsir/translation
 * 
 * Falls back to UthmanicHafs Unicode while per-page fonts load from CDN.
 */

import { useMemo } from 'react';
import { Volume2, Loader2, Share2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Ayah, SurahListItem, QuranWord as QuranWordType } from '@/types/quran';
import { useQcfFont, preloadAdjacentPageFonts } from '@/hooks/useQcfFont';
import QuranWord from './QuranWord';

type ReadMode = 'read' | 'tadabbur';

interface QuranPageViewProps {
  ayahs: Ayah[];
  currentPage: number;
  showTranslation: boolean;
  showTajweed?: boolean;
  fontSize: 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge' | 'xxxlarge';

  surahs?: SurahListItem[];
  readMode?: ReadMode;
  /** Resolved theme for QCF font palette (dark = dark palette for contrast on dark bg). */
  theme?: 'light' | 'dark';
  onAyahTap?: (surahNumber: number, ayahNumber: number) => void;
  onSurahHeaderTap?: (surahNumber: number) => void;
  /** Tadabbur only: play single ayah */
  onPlayAyah?: (surahNumber: number, ayahNumber: number) => void;
  /** Tadabbur only: share single ayah */
  onShareAyah?: (ayah: Ayah) => void;
  playingAyahKey?: string | null;
  loadingAyahKey?: string | null;
}

const fontSizeMap = {
  small: 'qcf-scale-small',
  medium: 'qcf-scale-medium',
  large: 'qcf-scale-large',
  xlarge: 'qcf-scale-xlarge',
  xxlarge: 'qcf-scale-xxlarge',
  xxxlarge: 'qcf-scale-xxxlarge',
};

/** Group all words from ayahs by page and lineNumber for Mushaf layout */
function groupWordsByLine(ayahs: Ayah[], currentPage: number): { pageNumber: number; lineNumber: number; words: (QuranWordType & { surahNumber?: number; ayahNumberInSurah?: number })[] }[] {
  const lineMap = new Map<string, (QuranWordType & { surahNumber?: number; ayahNumberInSurah?: number })[]>();

  for (const ayah of ayahs) {
    if (!ayah.words?.length) continue;
    for (const word of ayah.words) {
      const pageNum = word.pageNumber ?? currentPage;
      const lineNum = word.lineNumber ?? 0;
      const key = `${pageNum}_${lineNum}`;
      
      if (!lineMap.has(key)) {
        lineMap.set(key, []);
      }
      lineMap.get(key)!.push({
        ...word,
        surahNumber: ayah.surahNumber,
        ayahNumberInSurah: ayah.numberInSurah,
      });
    }
  }

  return Array.from(lineMap.entries())
    .map(([key, words]) => {
      const [p, l] = key.split('_').map(Number);
      return { pageNumber: p, lineNumber: l, words };
    })
    .sort((a, b) => {
      if (a.pageNumber !== b.pageNumber) return a.pageNumber - b.pageNumber;
      return a.lineNumber - b.lineNumber;
    });
}

/** Convert number to Arabic numerals (٠١٢٣٤٥٦٧٨٩) */
function toArabicNumerals(n: number): string {
  const arabicDigits = '٠١٢٣٤٥٦٧٨٩';
  return String(n).replace(/\d/g, (d) => arabicDigits[parseInt(d, 10)]);
}

/** Render Surah header */
function SurahHeader({
  surahNum,
  surahs,
  onSurahHeaderTap,
}: {
  surahNum: number;
  surahs: SurahListItem[];
  onSurahHeaderTap?: (surahNumber: number) => void;
}) {
  const surahInfo = surahs.find(s => s.number === surahNum);
  if (!surahInfo) return null;
  const showBismillah = surahNum !== 1 && surahNum !== 9;
  return (
    <div>
      <div
        className={cn('surah-header-ornament', onSurahHeaderTap && 'cursor-pointer')}
        onClick={() => onSurahHeaderTap?.(surahNum)}
      >
        <span className="surah-arabic-name">{surahInfo.name}</span>
        <span className="surah-english-name">
          {surahInfo.number}. {surahInfo.englishName} · {surahInfo.englishNameTranslation}
        </span>
      </div>
      {showBismillah && (
        <div className="text-center mb-3">
          <img
            src="/bismillah.png"
            alt="بِسۡمِ ٱللَّهِ ٱلرَّحۡمَـٰنِ ٱلرَّحِیمِ"
            className="inline-block h-16 w-auto opacity-90"
          />
        </div>
      )}
    </div>
  );
}

export function QuranPageView({
  ayahs,
  currentPage,
  showTranslation,
  showTajweed = false,
  fontSize,

  surahs = [],
  readMode = 'read',
  theme = 'light',
  onAyahTap,
  onSurahHeaderTap,
  onPlayAyah,
  playingAyahKey = null,
  loadingAyahKey = null,
  onShareAyah,
}: QuranPageViewProps) {
  // Collect all words for font loading
  const allWords = useMemo(
    () => ayahs.flatMap(a => a.words ?? []),
    [ayahs]
  );

  const fontVersion = showTajweed ? 'v4' as const : 'v2' as const;
  const loadedPages = useQcfFont(allWords, { version: fontVersion, theme });

  // Preload adjacent page fonts (same theme so palette matches)
  useMemo(() => {
    if (allWords.length > 0) {
      preloadAdjacentPageFonts(currentPage, fontVersion, theme);
    }
  }, [currentPage, fontVersion, theme, allWords.length]);

  // Group words by line for Mushaf layout
  const lines = useMemo(() => groupWordsByLine(ayahs, currentPage), [ayahs, currentPage]);

  // Track which line a new surah starts on (for read mode)
  const surahHeaderLines = useMemo(() => {
    const map = new Map<number, number>();
    for (const ayah of ayahs) {
      if (ayah.numberInSurah === 1 && ayah.surahNumber && ayah.words?.length) {
        const firstWordLine = ayah.words[0]?.lineNumber ?? 0;
        map.set(ayah.surahNumber, firstWordLine);
      }
    }
    return map;
  }, [ayahs]);

  // When parent passes no data yet, show a minimal placeholder (no skeleton flicker)
  if (!ayahs || ayahs.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground/50">
        <p className="text-sm">กำลังโหลด...</p>
      </div>
    );
  }

  const hasWordData = allWords.length > 0;

  return (
    <div className="w-full h-full">
      <div className="mx-auto px-1 py-6">
        {readMode === 'tadabbur' ? (
          /* ── Tadabbur Mode: Verse-by-verse with translations ── */
          <TadabburLayout
            ayahs={ayahs}
            surahs={surahs}
            fontSize={fontSize}
            fontVersion={fontVersion}
            loadedPages={loadedPages}
            hasWordData={hasWordData}
            theme={theme}
            onAyahTap={onAyahTap}
            onSurahHeaderTap={onSurahHeaderTap}
            onPlayAyah={onPlayAyah}
            playingAyahKey={playingAyahKey}
            loadingAyahKey={loadingAyahKey}
            onShareAyah={onShareAyah}
          />
        ) : hasWordData ? (
          /* ── Read Mode: Continuous Flow Layout ── */
          <div className={cn('mushaf-flow', fontSizeMap[fontSize])}>
            {(() => {
              // Build a flat list of renderable items: surah headers + words
              const items: React.ReactNode[] = [];
              const headersRendered = new Set<number>();

              for (const line of lines) {
                // Check for surah headers before this line
                surahHeaderLines.forEach((headerLine, surahNum) => {
                  if (headerLine === line.lineNumber && !headersRendered.has(surahNum)) {
                    headersRendered.add(surahNum);
                    items.push(
                      <SurahHeader
                        key={`header-${surahNum}`}
                        surahNum={surahNum}
                        surahs={surahs}
                        onSurahHeaderTap={onSurahHeaderTap}
                      />
                    );
                  }
                });

                // Render each word inline
                for (const [wordIdx, word] of line.words.entries()) {
                  items.push(
                    <QuranWord
                      key={`w-${word.id ?? wordIdx}-${word.position}-${line.lineNumber}`}
                      word={word}
                      isFontLoaded={loadedPages.has(word.pageNumber ?? 0)}
                      fontVersion={fontVersion}
                      theme={theme}
                      onClick={onAyahTap ? () => onAyahTap(word.surahNumber ?? 0, word.ayahNumberInSurah ?? 0) : undefined}
                    />
                  );
                }
              }

              return items;
            })()}
          </div>
        ) : (
          /* ── Fallback: Verse-level text rendering ── */
          <div className="space-y-4">
            {ayahs.map((ayah) => (
              <div
                key={ayah.number}
                className={cn('mushaf-line', fontSizeMap[fontSize])}
                style={{ fontFamily: "'UthmanicHafs', 'KFGQPC Uthmanic', serif" }}
                onClick={() => onAyahTap?.(ayah.surahNumber ?? 0, ayah.numberInSurah)}
              >
                <span>{ayah.text}</span>
              </div>
            ))}
          </div>
        )}

        {/* Per-ayah translations (only in read mode) */}
        {readMode === 'read' && showTranslation && (
          <div className="mt-4 space-y-2">
            {ayahs.map((ayah) =>
              ayah.translations && ayah.translations.length > 0 ? (
                <div
                  key={`tr-${ayah.number}`}
                  className="text-sm text-muted-foreground leading-relaxed px-2 py-1 border-l-2 border-primary/30"
                >
                  <span className="font-medium text-xs text-primary/60 mr-1">
                    {ayah.surahNumber}:{ayah.numberInSurah}
                  </span>
                  <span dangerouslySetInnerHTML={{ __html: ayah.translations[0].text }} />
                </div>
              ) : null
            )}
          </div>
        )}

        {/* Page number */}
        <div className="text-center py-4 text-sm text-muted-foreground">
          {toArabicNumerals(currentPage)}
        </div>
      </div>
    </div>
  );
}

/** Tadabbur Layout — verse-by-verse with inline translations */
function TadabburLayout({
  ayahs,
  surahs,
  fontSize,
  fontVersion,
  loadedPages,
  hasWordData,
  theme,
  onAyahTap,
  onSurahHeaderTap,
  onPlayAyah,
  playingAyahKey = null,
  loadingAyahKey = null,
  onShareAyah,
}: {
  ayahs: Ayah[];
  surahs: SurahListItem[];
  fontSize: 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge' | 'xxxlarge';
  fontVersion: 'v2' | 'v4';
  loadedPages: Set<number>;
  hasWordData: boolean;
  theme: 'light' | 'dark';
  onAyahTap?: (surahNumber: number, ayahNumber: number) => void;
  onSurahHeaderTap?: (surahNumber: number) => void;
  onPlayAyah?: (surahNumber: number, ayahNumber: number) => void;
  playingAyahKey?: string | null;
  loadingAyahKey?: string | null;
  onShareAyah?: (ayah: Ayah) => void;
}) {
  // Group ayahs by surah for headers
  const surahGroups: { surahNumber: number; ayahs: Ayah[] }[] = [];
  for (const ayah of ayahs) {
    const sn = ayah.surahNumber ?? 0;
    const last = surahGroups[surahGroups.length - 1];
    if (last && last.surahNumber === sn) {
      last.ayahs.push(ayah);
    } else {
      surahGroups.push({ surahNumber: sn, ayahs: [ayah] });
    }
  }

  return (
    <div className={cn('space-y-6', fontSizeMap[fontSize])}>
      {surahGroups.map((group) => {
        const showHeader = group.ayahs[0]?.numberInSurah === 1;
        return (
          <div key={`${group.surahNumber}-${group.ayahs[0]?.number}`}>
            {showHeader && (
              <SurahHeader
                surahNum={group.surahNumber}
                surahs={surahs}
                onSurahHeaderTap={onSurahHeaderTap}
              />
            )}

            {/* Verse-by-verse with inline translation */}
            {group.ayahs.map((ayah) => {
              const sn = ayah.surahNumber ?? 0;
              const an = ayah.numberInSurah;
              const ayahKey = `${sn}:${an}`;
              const isPlaying = playingAyahKey === ayahKey;
              const isLoading = loadingAyahKey === ayahKey;
              return (
                <div
                  key={ayah.number}
                  className="tadabbur-verse"
                  onClick={() => onAyahTap?.(sn, an)}
                >
                  <div className="flex items-start gap-2 w-full">
                    {/* Arabic text — render words if available */}
                    <div className="mushaf-line flex-1" style={{ justifyContent: 'flex-end' }}>
                      {hasWordData && ayah.words?.length ? (
                        ayah.words.map((word, wordIdx) => (
                          <QuranWord
                            key={`tw-${word.id ?? wordIdx}-${word.position}`}
                            word={word}
                            isFontLoaded={loadedPages.has(word.pageNumber ?? 0)}
                            fontVersion={fontVersion}
                            theme={theme}
                            onClick={onAyahTap ? () => onAyahTap(sn, an) : undefined}
                          />
                        ))
                      ) : (
                        <span style={{ fontFamily: "'UthmanicHafs', 'KFGQPC Uthmanic', serif" }}>
                          {ayah.text}
                        </span>
                      )}
                    </div>
                    {/* Action buttons — tadabbur only */}
                    {(onShareAyah || onPlayAyah) && (
                      <div className="flex flex-col gap-1 shrink-0">
                        {onShareAyah && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onShareAyah(ayah);
                            }}
                            className="p-2 rounded-full text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                            title="แชร์อายะฮ์ไปยัง IG"
                            aria-label="แชร์อายะฮ์ไปยัง IG"
                          >
                            <Share2 className="w-5 h-5" />
                          </button>
                        )}
                        {onPlayAyah && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onPlayAyah(sn, an);
                            }}
                            className={cn(
                              'p-2 rounded-full text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors',
                              isPlaying && 'text-primary bg-primary/10'
                            )}
                            title="ฟังอายะฮ์"
                            aria-label="ฟังอายะฮ์"
                          >
                            {isLoading ? (
                              <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                              <Volume2 className="w-5 h-5" />
                            )}
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Translation below each ayah */}
                  {ayah.translations && ayah.translations.length > 0 && (
                    <div className="tadabbur-translation">
                      <span className="tadabbur-verse-num">
                        {toArabicNumerals(ayah.numberInSurah)}
                      </span>
                      <span dangerouslySetInnerHTML={{ __html: ayah.translations[0].text }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

export default QuranPageView;
